import { useState, useEffect, useRef } from "react";
import { createClient } from "@supabase/supabase-js";

// ── Supabase ──────────────────────────────────────────────────────────────────
// Configura estas variables en tu .env.local o en Vercel:
// REACT_APP_SUPABASE_URL=...
// REACT_APP_SUPABASE_ANON_KEY=...
const supabase = createClient(
  process.env.REACT_APP_SUPABASE_URL || "https://TU_PROYECTO.supabase.co",
  process.env.REACT_APP_SUPABASE_ANON_KEY || "TU_ANON_KEY"
);

// ── Constantes visuales ───────────────────────────────────────────────────────
const CORAL = "#FF6B6B";
const TEAL = "#5BAD9A";
const TEAL_DARK = "#2d6b5e";
const INDIGO = "#6C5CE7";
const ORANGE = "#FF8E53";
const YELLOW = "#FFD93D";

// ── Mascota SVG Solfi ─────────────────────────────────────────────────────────
function SolfiMascot({ size = 80, animate = false }) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 120 160"
      fill="none"
      xmlns="http://www.w3.org/2000/svg"
      style={animate ? { animation: "solfi-float 3s ease-in-out infinite" } : {}}
    >
      {/* Cuerpo nota musical */}
      <ellipse cx="55" cy="100" rx="28" ry="24" fill={TEAL} />
      <rect x="79" y="40" width="10" height="62" rx="5" fill={TEAL} />
      <rect x="79" y="38" width="30" height="14" rx="5" fill={TEAL} />
      {/* Cara */}
      <circle cx="47" cy="96" r="5" fill="#1a3d35" />
      <circle cx="63" cy="96" r="5" fill="#1a3d35" />
      <circle cx="49" cy="94" r="2" fill="white" />
      <circle cx="65" cy="94" r="2" fill="white" />
      <path d="M46 104 Q55 112 64 104" stroke="#1a3d35" strokeWidth="2.5" strokeLinecap="round" fill="none" />
      {/* Mejillas */}
      <ellipse cx="42" cy="102" rx="6" ry="4" fill="#FFB8B8" opacity="0.6" />
      <ellipse cx="68" cy="102" rx="6" ry="4" fill="#FFB8B8" opacity="0.6" />
      {/* Brazos */}
      <path d="M28 105 Q18 110 20 118" stroke={TEAL} strokeWidth="5" strokeLinecap="round" fill="none" />
      <path d="M82 105 Q92 110 90 118" stroke={TEAL} strokeWidth="5" strokeLinecap="round" fill="none" />
      {/* Piernas */}
      <path d="M45 122 Q42 138 38 145" stroke={TEAL} strokeWidth="5" strokeLinecap="round" fill="none" />
      <path d="M65 122 Q68 138 72 145" stroke={TEAL} strokeWidth="5" strokeLinecap="round" fill="none" />
      {/* Zapatos */}
      <ellipse cx="36" cy="147" rx="9" ry="5" fill="#1a3d35" />
      <ellipse cx="74" cy="147" rx="9" ry="5" fill="#1a3d35" />
      {/* Correa mochila */}
      <path d="M72 88 Q85 95 82 108" stroke="#3d8c7c" strokeWidth="3" fill="none" />
    </svg>
  );
}

// ── Nota musical decorativa ───────────────────────────────────────────────────
function FloatingNote({ char, style }) {
  return (
    <span
      style={{
        position: "absolute",
        fontSize: 24,
        opacity: 0.12,
        userSelect: "none",
        pointerEvents: "none",
        ...style,
      }}
    >
      {char}
    </span>
  );
}

// ── Simulador de ejercicio (loop) ─────────────────────────────────────────────
function ExerciseDemo() {
  const notes = ["Do", "Re", "Mi", "Fa", "Sol", "La", "Si"];
  const colors = [CORAL, ORANGE, YELLOW, TEAL, INDIGO, "#A29BFE", "#FD79A8"];
  const [current, setCurrent] = useState(0);
  const [answered, setAnswered] = useState(null);
  const [step, setStep] = useState("question"); // question | correct | wrong
  const options = [1, 3, 5];

  useEffect(() => {
    if (step === "correct" || step === "wrong") {
      const t = setTimeout(() => {
        setCurrent((c) => (c + 1) % notes.length);
        setAnswered(null);
        setStep("question");
      }, 1400);
      return () => clearTimeout(t);
    }
  }, [step]);

  useEffect(() => {
    if (step === "question") {
      const t = setTimeout(() => {
        // Auto-responde correctamente para el loop demo
        setAnswered(0);
        setStep("correct");
      }, 2000);
      return () => clearTimeout(t);
    }
  }, [step, current]);

  const optionNotes = options.map((offset) => notes[(current + offset) % notes.length]);
  const allOptions = [notes[current], ...optionNotes].sort(() => Math.random() - 0.5);
  // usamos índice fijo para evitar re-render
  const displayOptions = [notes[current], notes[(current + 2) % notes.length], notes[(current + 4) % notes.length]];

  return (
    <div
      style={{
        background: "white",
        borderRadius: 24,
        padding: "28px 24px",
        boxShadow: "0 20px 60px rgba(45,107,94,0.15)",
        maxWidth: 340,
        margin: "0 auto",
        position: "relative",
        overflow: "hidden",
      }}
    >
      {/* Header ejercicio */}
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 16 }}>
        <span style={{ fontSize: 12, fontWeight: 800, color: TEAL, background: "#e8f8f4", padding: "4px 10px", borderRadius: 99 }}>
          🎹 Lectura de Notas
        </span>
        <span style={{ fontSize: 14, letterSpacing: 3 }}>❤️❤️❤️</span>
      </div>

      {/* Pentagrama simulado */}
      <div
        style={{
          background: "#fafafa",
          borderRadius: 16,
          padding: "20px 16px",
          marginBottom: 20,
          position: "relative",
          minHeight: 100,
        }}
      >
        {/* Líneas del pentagrama */}
        {[0, 1, 2, 3, 4].map((i) => (
          <div
            key={i}
            style={{
              position: "absolute",
              left: 20,
              right: 20,
              top: 20 + i * 14,
              height: 1.5,
              background: "#ddd",
            }}
          />
        ))}
        {/* Nota */}
        <div
          style={{
            position: "absolute",
            left: "50%",
            top: 22 + (current % 5) * 14 - 10,
            transform: "translateX(-50%)",
            width: 22,
            height: 16,
            borderRadius: "50%",
            background: step === "correct" ? TEAL : step === "wrong" ? CORAL : TEAL_DARK,
            border: "2px solid " + TEAL_DARK,
            transition: "all 0.3s",
          }}
        />
        {/* Plica */}
        <div
          style={{
            position: "absolute",
            left: "calc(50% + 8px)",
            top: 10 + (current % 5) * 14 - 10,
            width: 2,
            height: 36,
            background: TEAL_DARK,
          }}
        />
        {/* Clave de Sol */}
        <div style={{ position: "absolute", left: 24, top: 8, fontSize: 52, lineHeight: 1, color: "#555", fontFamily: "serif" }}>𝄞</div>
      </div>

      {/* Pregunta */}
      <p style={{ textAlign: "center", fontSize: 14, fontWeight: 700, color: "#666", marginBottom: 14 }}>
        ¿Qué nota es esta?
      </p>

      {/* Opciones */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
        {displayOptions.map((note, i) => {
          const isCorrect = note === notes[current];
          const isSelected = answered === i;
          let bg = "#f0f0f0";
          let color = "#444";
          if (isSelected && step === "correct") { bg = TEAL; color = "white"; }
          if (isSelected && step === "wrong") { bg = CORAL; color = "white"; }
          if (!isSelected && step === "correct" && isCorrect) { bg = "#e8f8f4"; color = TEAL; }
          return (
            <button
              key={i}
              style={{
                background: bg,
                color,
                border: "none",
                borderRadius: 12,
                padding: "12px 8px",
                fontSize: 15,
                fontWeight: 800,
                cursor: "pointer",
                fontFamily: "inherit",
                transition: "all 0.2s",
                transform: isSelected ? "scale(1.06)" : "scale(1)",
              }}
            >
              {note}
            </button>
          );
        })}
      </div>

      {/* Feedback Solfi */}
      {step === "correct" && (
        <div
          style={{
            marginTop: 14,
            background: "#e8f8f4",
            borderRadius: 12,
            padding: "8px 12px",
            display: "flex",
            alignItems: "center",
            gap: 8,
            animation: "fadeUp 0.3s ease",
          }}
        >
          <SolfiMascot size={28} />
          <span style={{ fontSize: 13, fontWeight: 700, color: TEAL_DARK }}>¡Perfecto! 🎵 +20 XP</span>
        </div>
      )}

      {/* Barra de progreso */}
      <div style={{ marginTop: 14, background: "#f0f0f0", borderRadius: 99, height: 6, overflow: "hidden" }}>
        <div
          style={{
            width: `${((current + 1) / notes.length) * 100}%`,
            height: "100%",
            background: `linear-gradient(90deg, ${TEAL}, ${INDIGO})`,
            borderRadius: 99,
            transition: "width 0.5s ease",
          }}
        />
      </div>
      <div style={{ textAlign: "center", fontSize: 11, color: "#999", marginTop: 4, fontWeight: 700 }}>
        {current + 1} / {notes.length}
      </div>
    </div>
  );
}

// ── Formulario de contacto ────────────────────────────────────────────────────
function ContactForm() {
  const [form, setForm] = useState({ nombre: "", email: "", tipo: "alumno", mensaje: "" });
  const [status, setStatus] = useState("idle"); // idle | loading | success | error

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.nombre || !form.email) return;
    setStatus("loading");
    try {
      // 1. Guardar en Supabase
      const { error } = await supabase
        .from("contactos")
        .insert([{ ...form, created_at: new Date().toISOString() }]);
      if (error) throw error;

      // 2. Notificación email vía API route (Resend)
      // Si tienes /api/contact en tu backend:
      // await fetch("/api/contact", { method: "POST", body: JSON.stringify(form), headers: { "Content-Type": "application/json" } });

      setStatus("success");
      setForm({ nombre: "", email: "", tipo: "alumno", mensaje: "" });
    } catch (err) {
      console.error(err);
      setStatus("error");
    }
  };

  const inputStyle = {
    width: "100%",
    padding: "14px 16px",
    borderRadius: 14,
    border: "2px solid #e8f5f2",
    fontSize: 14,
    fontFamily: "inherit",
    fontWeight: 600,
    color: TEAL_DARK,
    background: "white",
    outline: "none",
    transition: "border-color 0.2s",
    boxSizing: "border-box",
  };

  return (
    <div
      style={{
        background: "white",
        borderRadius: 28,
        padding: "40px 36px",
        boxShadow: "0 20px 60px rgba(45,107,94,0.12)",
        maxWidth: 480,
        margin: "0 auto",
      }}
    >
      <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 24 }}>
        <SolfiMascot size={44} />
        <div>
          <h3 style={{ margin: 0, fontSize: 20, fontFamily: "'Baloo 2', cursive", color: TEAL_DARK }}>
            ¡Escríbenos!
          </h3>
          <p style={{ margin: 0, fontSize: 13, color: "#888", fontWeight: 600 }}>
            Te responderemos en menos de 24h
          </p>
        </div>
      </div>

      {status === "success" ? (
        <div style={{ textAlign: "center", padding: "32px 16px" }}>
          <div style={{ fontSize: 52, marginBottom: 12 }}>🎉</div>
          <h3 style={{ color: TEAL_DARK, fontFamily: "'Baloo 2', cursive" }}>¡Mensaje recibido!</h3>
          <p style={{ color: "#666", fontSize: 14 }}>
            Pronto te contactaremos. ¡Gracias por tu interés en Solfeante!
          </p>
          <button
            onClick={() => setStatus("idle")}
            style={{ marginTop: 12, background: TEAL, color: "white", border: "none", borderRadius: 12, padding: "10px 24px", fontFamily: "inherit", fontSize: 14, fontWeight: 800, cursor: "pointer" }}
          >
            Enviar otro mensaje
          </button>
        </div>
      ) : (
        <form onSubmit={handleSubmit}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12, marginBottom: 12 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 800, color: "#999", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Nombre</label>
              <input
                style={inputStyle}
                value={form.nombre}
                onChange={(e) => setForm({ ...form, nombre: e.target.value })}
                placeholder="Tu nombre"
                required
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 800, color: "#999", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Email</label>
              <input
                type="email"
                style={inputStyle}
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                placeholder="tu@email.com"
                required
              />
            </div>
          </div>

          <div style={{ marginBottom: 12 }}>
            <label style={{ fontSize: 12, fontWeight: 800, color: "#999", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Soy...</label>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 8 }}>
              {[{ val: "alumno", label: "🎓 Alumno" }, { val: "profesor", label: "👩‍🏫 Profesor" }, { val: "escuela", label: "🏫 Escuela" }].map((opt) => (
                <button
                  key={opt.val}
                  type="button"
                  onClick={() => setForm({ ...form, tipo: opt.val })}
                  style={{
                    padding: "10px 8px",
                    borderRadius: 12,
                    border: `2px solid ${form.tipo === opt.val ? TEAL : "#e8f5f2"}`,
                    background: form.tipo === opt.val ? "#e8f8f4" : "white",
                    color: form.tipo === opt.val ? TEAL_DARK : "#999",
                    fontSize: 12,
                    fontWeight: 800,
                    cursor: "pointer",
                    fontFamily: "inherit",
                    transition: "all 0.15s",
                  }}
                >
                  {opt.label}
                </button>
              ))}
            </div>
          </div>

          <div style={{ marginBottom: 20 }}>
            <label style={{ fontSize: 12, fontWeight: 800, color: "#999", display: "block", marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Mensaje</label>
            <textarea
              style={{ ...inputStyle, minHeight: 100, resize: "vertical" }}
              value={form.mensaje}
              onChange={(e) => setForm({ ...form, mensaje: e.target.value })}
              placeholder="¿En qué podemos ayudarte?"
            />
          </div>

          <button
            type="submit"
            disabled={status === "loading"}
            style={{
              width: "100%",
              padding: "16px",
              borderRadius: 16,
              border: "none",
              background: status === "loading" ? "#aaa" : `linear-gradient(135deg, ${TEAL}, ${TEAL_DARK})`,
              color: "white",
              fontSize: 16,
              fontWeight: 800,
              fontFamily: "inherit",
              cursor: status === "loading" ? "not-allowed" : "pointer",
              transition: "all 0.2s",
              letterSpacing: 0.3,
            }}
          >
            {status === "loading" ? "Enviando..." : "Enviar mensaje 🎵"}
          </button>

          {status === "error" && (
            <p style={{ color: CORAL, textAlign: "center", fontSize: 13, marginTop: 10 }}>
              Hubo un error. Inténtalo de nuevo o escríbenos a hola@solfeante.com
            </p>
          )}
        </form>
      )}
    </div>
  );
}

// ── Feature Card ──────────────────────────────────────────────────────────────
function FeatureCard({ icon, title, desc, color, delay }) {
  return (
    <div
      style={{
        background: "white",
        borderRadius: 24,
        padding: "28px 24px",
        boxShadow: "0 4px 24px rgba(45,107,94,0.08)",
        borderTop: `4px solid ${color}`,
        animation: `fadeUp 0.6s ease ${delay}s both`,
        transition: "transform 0.2s, box-shadow 0.2s",
      }}
      onMouseEnter={(e) => { e.currentTarget.style.transform = "translateY(-4px)"; e.currentTarget.style.boxShadow = "0 16px 48px rgba(45,107,94,0.14)"; }}
      onMouseLeave={(e) => { e.currentTarget.style.transform = "translateY(0)"; e.currentTarget.style.boxShadow = "0 4px 24px rgba(45,107,94,0.08)"; }}
    >
      <div style={{ fontSize: 36, marginBottom: 14 }}>{icon}</div>
      <h3 style={{ margin: "0 0 8px", fontSize: 17, fontFamily: "'Baloo 2', cursive", color: TEAL_DARK }}>{title}</h3>
      <p style={{ margin: 0, fontSize: 14, color: "#666", lineHeight: 1.6 }}>{desc}</p>
    </div>
  );
}

// ── Testimonial ───────────────────────────────────────────────────────────────
function Testimonial({ avatar, name, role, text }) {
  return (
    <div style={{ background: "white", borderRadius: 20, padding: "24px", boxShadow: "0 4px 20px rgba(45,107,94,0.08)" }}>
      <p style={{ margin: "0 0 16px", fontSize: 14, color: "#555", lineHeight: 1.7, fontStyle: "italic" }}>
        "{text}"
      </p>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <div style={{ width: 40, height: 40, borderRadius: "50%", background: `linear-gradient(135deg, ${TEAL}, ${INDIGO})`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 20 }}>
          {avatar}
        </div>
        <div>
          <div style={{ fontWeight: 800, fontSize: 14, color: TEAL_DARK }}>{name}</div>
          <div style={{ fontSize: 12, color: "#999" }}>{role}</div>
        </div>
      </div>
    </div>
  );
}

// ── MAIN APP ──────────────────────────────────────────────────────────────────
export default function App() {
  const [menuOpen, setMenuOpen] = useState(false);

  const scrollTo = (id) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
    setMenuOpen(false);
  };

  return (
    <div style={{ fontFamily: "'Nunito', sans-serif", background: "#f4fbf9", color: TEAL_DARK, overflowX: "hidden" }}>
      <style>{`
        @import url('https://fonts.googleapis.com/css2?family=Nunito:wght@400;600;700;800;900&family=Baloo+2:wght@700;800;900&display=swap');

        * { box-sizing: border-box; }

        @keyframes solfi-float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-12px); }
        }
        @keyframes fadeUp {
          from { opacity: 0; transform: translateY(20px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        @keyframes spin-slow {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        @keyframes note-float {
          0%   { opacity: 0; transform: translateY(0) rotate(0deg); }
          20%  { opacity: 0.15; }
          80%  { opacity: 0.15; }
          100% { opacity: 0; transform: translateY(-60px) rotate(20deg); }
        }

        html { scroll-behavior: smooth; }

        .nav-link {
          color: ${TEAL_DARK};
          text-decoration: none;
          font-weight: 800;
          font-size: 14px;
          padding: 8px 14px;
          border-radius: 10px;
          cursor: pointer;
          transition: background 0.15s;
        }
        .nav-link:hover { background: rgba(91,173,154,0.12); }

        .cta-primary {
          background: linear-gradient(135deg, ${CORAL}, ${ORANGE});
          color: white;
          border: none;
          border-radius: 18px;
          padding: 16px 36px;
          font-size: 17px;
          font-weight: 800;
          font-family: inherit;
          cursor: pointer;
          transition: transform 0.2s, box-shadow 0.2s;
          box-shadow: 0 8px 28px rgba(255,107,107,0.35);
        }
        .cta-primary:hover {
          transform: translateY(-2px);
          box-shadow: 0 14px 36px rgba(255,107,107,0.45);
        }

        .cta-secondary {
          background: white;
          color: ${TEAL_DARK};
          border: 2.5px solid ${TEAL};
          border-radius: 18px;
          padding: 14px 32px;
          font-size: 16px;
          font-weight: 800;
          font-family: inherit;
          cursor: pointer;
          transition: all 0.2s;
        }
        .cta-secondary:hover {
          background: #e8f8f4;
          transform: translateY(-2px);
        }

        .exercise-type {
          display: flex;
          align-items: center;
          gap: 14px;
          background: white;
          borderRadius: 16px;
          padding: 18px 20px;
          box-shadow: 0 2px 14px rgba(45,107,94,0.07);
          transition: transform 0.15s;
          cursor: default;
        }
        .exercise-type:hover { transform: translateX(4px); }

        @media (max-width: 768px) {
          .hero-grid { flex-direction: column !important; }
          .features-grid { grid-template-columns: 1fr !important; }
          .testimonials-grid { grid-template-columns: 1fr !important; }
          .stats-row { flex-direction: column !important; gap: 16px !important; }
          .nav-links-desktop { display: none !important; }
        }
      `}</style>

      {/* ── NAV ── */}
      <nav style={{
        position: "sticky",
        top: 0,
        zIndex: 200,
        background: "rgba(244,251,249,0.92)",
        backdropFilter: "blur(12px)",
        borderBottom: "1.5px solid rgba(91,173,154,0.15)",
        padding: "0 5vw",
        height: 68,
        display: "flex",
        alignItems: "center",
        justifyContent: "space-between",
      }}>
        <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
          <SolfiMascot size={38} />
          <span style={{ fontFamily: "'Baloo 2', cursive", fontSize: 24, fontWeight: 900, color: TEAL_DARK }}>
            Solfeante
          </span>
        </div>

        <div className="nav-links-desktop" style={{ display: "flex", gap: 4 }}>
          {[["¿Qué es?", "que-es"], ["Funcionalidades", "funcionalidades"], ["Para profesores", "profesores"], ["Testimonios", "testimonios"], ["Contacto", "contacto"]].map(([label, id]) => (
            <span key={id} className="nav-link" onClick={() => scrollTo(id)}>{label}</span>
          ))}
        </div>

        <button className="cta-primary" style={{ padding: "10px 22px", fontSize: 14, borderRadius: 12, boxShadow: "0 4px 16px rgba(255,107,107,0.3)" }}
          onClick={() => window.open("https://plataforma.solfeante.com", "_blank")}>
          Entrar a la plataforma →
        </button>
      </nav>

      {/* ── HERO ── */}
      <section style={{ padding: "80px 5vw 60px", position: "relative", overflow: "hidden" }}>
        {/* Fondo decorativo */}
        <div style={{ position: "absolute", top: -100, right: -100, width: 500, height: 500, borderRadius: "50%", background: `radial-gradient(circle, ${TEAL}22, transparent 70%)`, pointerEvents: "none" }} />
        <div style={{ position: "absolute", bottom: -80, left: -80, width: 400, height: 400, borderRadius: "50%", background: `radial-gradient(circle, ${CORAL}18, transparent 70%)`, pointerEvents: "none" }} />

        {/* Notas flotantes */}
        {["♩", "♪", "♫", "♬", "𝄞", "♩", "♪"].map((n, i) => (
          <FloatingNote key={i} char={n} style={{ top: `${10 + i * 12}%`, left: `${3 + i * 13}%`, fontSize: 28 + i * 4 }} />
        ))}

        <div className="hero-grid" style={{ display: "flex", alignItems: "center", gap: 60, maxWidth: 1100, margin: "0 auto" }}>
          {/* Texto */}
          <div style={{ flex: 1, animation: "fadeUp 0.7s ease both" }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: `${TEAL}18`, borderRadius: 99, padding: "6px 16px", marginBottom: 24, fontSize: 13, fontWeight: 800, color: TEAL }}>
              🎵 Plataforma de lenguaje musical con IA
            </div>

            <h1 style={{ fontFamily: "'Baloo 2', cursive", fontSize: "clamp(36px, 5vw, 58px)", fontWeight: 900, lineHeight: 1.1, margin: "0 0 20px", color: TEAL_DARK }}>
              Aprende solfeo{" "}
              <span style={{ background: `linear-gradient(135deg, ${CORAL}, ${ORANGE})`, WebkitBackgroundClip: "text", WebkitTextFillColor: "transparent" }}>
                de verdad
              </span>
              <br />con tu propio ritmo
            </h1>

            <p style={{ fontSize: 18, lineHeight: 1.7, color: "#555", maxWidth: 480, margin: "0 0 36px" }}>
              Solfeante combina ejercicios interactivos, feedback inmediato y la guía de{" "}
              <strong style={{ color: TEAL }}>Solfi</strong>, tu asistente de IA musical, para que progreses más rápido y con más diversión.
            </p>

            <div style={{ display: "flex", gap: 14, flexWrap: "wrap" }}>
              <button className="cta-primary" onClick={() => window.open("https://plataforma.solfeante.com", "_blank")}>
                Empezar gratis 🎉
              </button>
              <button className="cta-secondary" onClick={() => scrollTo("que-es")}>
                Ver cómo funciona
              </button>
            </div>

            {/* Stats */}
            <div className="stats-row" style={{ display: "flex", gap: 32, marginTop: 40 }}>
              {[["📚", "15", "Unidades"], ["🎹", "6", "Tipos de ejercicio"], ["🤖", "1", "IA incluida"]].map(([icon, num, label]) => (
                <div key={label}>
                  <div style={{ fontSize: 22, fontFamily: "'Baloo 2', cursive", fontWeight: 900, color: TEAL_DARK }}>
                    {icon} {num}
                  </div>
                  <div style={{ fontSize: 12, color: "#999", fontWeight: 700 }}>{label}</div>
                </div>
              ))}
            </div>
          </div>

          {/* Demo interactivo */}
          <div style={{ flex: 1, maxWidth: 380, animation: "fadeUp 0.7s ease 0.2s both" }}>
            <div style={{ position: "relative" }}>
              {/* Badge "Demo en vivo" */}
              <div style={{ position: "absolute", top: -16, right: 10, background: CORAL, color: "white", borderRadius: 99, padding: "4px 14px", fontSize: 11, fontWeight: 800, zIndex: 10, boxShadow: "0 4px 12px rgba(255,107,107,0.4)" }}>
                🔴 Demo en vivo
              </div>
              <ExerciseDemo />
            </div>

            {/* Solfi flotando */}
            <div style={{ textAlign: "center", marginTop: 20 }}>
              <SolfiMascot size={64} animate />
              <p style={{ margin: "8px 0 0", fontSize: 13, color: TEAL, fontWeight: 800 }}>Solfi · Tu asistente musical IA</p>
            </div>
          </div>
        </div>
      </section>

      {/* ── QUÉ ES ── */}
      <section id="que-es" style={{ padding: "80px 5vw", background: "white" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontFamily: "'Baloo 2', cursive", fontSize: "clamp(28px, 4vw, 44px)", color: TEAL_DARK, margin: "0 0 16px" }}>
            ¿Qué es Solfeante?
          </h2>
          <p style={{ fontSize: 17, color: "#666", lineHeight: 1.8, maxWidth: 640, margin: "0 auto 48px" }}>
            Una plataforma educativa diseñada para estudiantes de conservatorio y academias de música.
            Practica teoría, ritmo, lectura de notas y dictados — con ejercicios adaptativos y el seguimiento de tu profesor.
          </p>

          {/* Tipos de ejercicio */}
          <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(240px, 1fr))", gap: 12, textAlign: "left" }}>
            {[
              { icon: "📖", name: "Teoría musical", desc: "Conceptos clave explicados por Solfi", color: INDIGO },
              { icon: "🎹", name: "Agilidad de notas", desc: "Reconoce notas en el pentagrama", color: TEAL },
              { icon: "🥁", name: "Ritmo", desc: "Compases, valores y subdivisión", color: ORANGE },
              { icon: "🎵", name: "Lectura de notas", desc: "Clave de Sol y Fa en tiempo real", color: CORAL },
              { icon: "🎶", name: "Lectura melódica", desc: "Interpreta melodías completas", color: "#FD79A8" },
              { icon: "👂", name: "Dictados", desc: "Entrena tu oído musical", color: YELLOW },
            ].map((ex) => (
              <div key={ex.name} className="exercise-type" style={{ borderLeft: `4px solid ${ex.color}`, borderRadius: 16, padding: "16px 18px" }}>
                <span style={{ fontSize: 28 }}>{ex.icon}</span>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 14, color: TEAL_DARK }}>{ex.name}</div>
                  <div style={{ fontSize: 12, color: "#888", marginTop: 2 }}>{ex.desc}</div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ── FUNCIONALIDADES ── */}
      <section id="funcionalidades" style={{ padding: "80px 5vw", background: "#f4fbf9" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 52 }}>
            <h2 style={{ fontFamily: "'Baloo 2', cursive", fontSize: "clamp(28px, 4vw, 44px)", color: TEAL_DARK, margin: "0 0 12px" }}>
              Todo lo que necesitas para aprender
            </h2>
            <p style={{ fontSize: 16, color: "#888", maxWidth: 500, margin: "0 auto" }}>
              Diseñado pensando tanto en el alumno como en el profesor
            </p>
          </div>

          <div className="features-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20 }}>
            <FeatureCard icon="🤖" title="Solfi, tu IA musical" desc="Solfi te acompaña en cada ejercicio: te da pistas cuando te equivocas, te felicita cuando aciertas y adapta el nivel a tu progreso." color={TEAL} delay={0} />
            <FeatureCard icon="📊" title="Progreso visual" desc="Barras de progreso por unidad y tipo de ejercicio. Sabe exactamente dónde estás y qué te falta para la siguiente unidad." color={INDIGO} delay={0.1} />
            <FeatureCard icon="🔒" title="Desbloqueo progresivo" desc="Las unidades se desbloquean al completar el 80% de la anterior. Avanza a tu ritmo sin saltarte fundamentos." color={ORANGE} delay={0.2} />
            <FeatureCard icon="❤️" title="Sistema de vidas" desc="Cada sesión tiene sus vidas. El error tiene consecuencias pero nunca es frustrante — Solfi siempre te ayuda a entender." color={CORAL} delay={0.3} />
            <FeatureCard icon="⚡" title="XP y racha diaria" desc="Gana puntos de experiencia con cada ejercicio. Mantén tu racha diaria y sube en el ranking de tu clase." color={YELLOW} delay={0.4} />
            <FeatureCard icon="📱" title="Funciona en móvil" desc="Diseñada mobile-first. Practica desde el móvil, tablet u ordenador — siempre con la misma experiencia fluida." color="#FD79A8" delay={0.5} />
          </div>
        </div>
      </section>

      {/* ── PARA PROFESORES ── */}
      <section id="profesores" style={{ padding: "80px 5vw", background: "white" }}>
        <div style={{ maxWidth: 1000, margin: "0 auto", display: "flex", alignItems: "center", gap: 60, flexWrap: "wrap" }}>
          {/* Visual panel profesor */}
          <div style={{ flex: 1, minWidth: 280 }}>
            <div style={{ background: "#f4fbf9", borderRadius: 24, padding: 28, boxShadow: "0 8px 32px rgba(45,107,94,0.1)" }}>
              <h4 style={{ margin: "0 0 16px", fontFamily: "'Baloo 2', cursive", color: TEAL_DARK, fontSize: 17 }}>👩‍🏫 Panel del Profesor</h4>
              {/* Stats */}
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 16 }}>
                {[{ icon: "👧", val: "38", label: "Alumnos" }, { icon: "📚", val: "5", label: "Niveles activos" }, { icon: "⏱️", val: "21 min", label: "Tiempo promedio" }, { icon: "⚠️", val: "4", label: "Sin actividad" }].map((s) => (
                  <div key={s.label} style={{ background: "white", borderRadius: 14, padding: "14px 16px", textAlign: "center", boxShadow: "0 2px 8px rgba(45,107,94,0.06)" }}>
                    <div style={{ fontSize: 22 }}>{s.icon}</div>
                    <div style={{ fontWeight: 900, fontSize: 20, color: TEAL_DARK }}>{s.val}</div>
                    <div style={{ fontSize: 11, color: "#999", fontWeight: 700 }}>{s.label}</div>
                  </div>
                ))}
              </div>
              {/* Alerta Solfi */}
              <div style={{ background: "#fff8e8", borderRadius: 14, padding: "12px 14px", display: "flex", gap: 10, alignItems: "flex-start" }}>
                <SolfiMascot size={32} />
                <p style={{ margin: 0, fontSize: 12, color: "#856404", fontWeight: 700 }}>
                  📊 <strong>3 alumnos</strong> llevan más de 3 días sin entrar.<br />
                  <strong>Dictados</strong> tiene el promedio más bajo del grupo (68%).
                </p>
              </div>
            </div>
          </div>

          {/* Texto */}
          <div style={{ flex: 1, minWidth: 280 }}>
            <div style={{ display: "inline-flex", alignItems: "center", gap: 8, background: `${INDIGO}18`, borderRadius: 99, padding: "6px 16px", marginBottom: 20, fontSize: 13, fontWeight: 800, color: INDIGO }}>
              👩‍🏫 Para profesores y academias
            </div>
            <h2 style={{ fontFamily: "'Baloo 2', cursive", fontSize: "clamp(24px, 3vw, 38px)", color: TEAL_DARK, margin: "0 0 16px", lineHeight: 1.2 }}>
              Tú pones el currículo,<br />Solfi hace el seguimiento
            </h2>
            <p style={{ fontSize: 16, color: "#666", lineHeight: 1.7, marginBottom: 24 }}>
              El panel del profesor te da visibilidad total: quién practica, cuánto tiempo, qué le cuesta más. Solfi te avisa automáticamente de los alumnos que necesitan atención.
            </p>
            <ul style={{ listStyle: "none", padding: 0, margin: 0 }}>
              {[
                "Asigna ejercicios específicos a cada alumno",
                "Desbloquea unidades manualmente si lo necesitas",
                "Recibe alertas de alumnos sin actividad",
                "Historial completo de sesiones y puntuaciones",
                "Compatible con varios grupos y niveles",
              ].map((item) => (
                <li key={item} style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10, fontSize: 14, color: "#555" }}>
                  <span style={{ color: TEAL, fontSize: 18, flexShrink: 0 }}>✓</span>
                  <span style={{ fontWeight: 700 }}>{item}</span>
                </li>
              ))}
            </ul>
          </div>
        </div>
      </section>

      {/* ── TESTIMONIOS ── */}
      <section id="testimonios" style={{ padding: "80px 5vw", background: "#f4fbf9" }}>
        <div style={{ maxWidth: 900, margin: "0 auto", textAlign: "center" }}>
          <h2 style={{ fontFamily: "'Baloo 2', cursive", fontSize: "clamp(28px, 4vw, 40px)", color: TEAL_DARK, margin: "0 0 12px" }}>
            Lo que dicen nuestros alumnos
          </h2>
          <p style={{ fontSize: 15, color: "#888", marginBottom: 44 }}>En fase beta — los primeros resultados hablan por sí solos</p>
          <div className="testimonials-grid" style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 20, textAlign: "left" }}>
            <Testimonial avatar="🎓" name="Sofía M." role="Alumna, 1º Conservatorio" text="Con Solfeante entendí los compases de una vez. Solfi me explica los errores de una forma que mi profe nunca tuvo tiempo de hacer." />
            <Testimonial avatar="👩‍🏫" name="Carmen R." role="Profesora de solfeo" text="Puedo ver de un vistazo qué ejercicios están flojeando mis alumnos. Me ahorra horas de corrección y tengo datos reales para preparar la clase." />
            <Testimonial avatar="🎵" name="Mateo G." role="Alumno, preparatorio" text="La racha diaria me engancha. Llevo 18 días seguidos practicando y ya reconozco las notas en el pentagrama mucho más rápido." />
          </div>
        </div>
      </section>

      {/* ── CONTACTO ── */}
      <section id="contacto" style={{ padding: "80px 5vw", background: "white" }}>
        <div style={{ maxWidth: 900, margin: "0 auto" }}>
          <div style={{ textAlign: "center", marginBottom: 48 }}>
            <h2 style={{ fontFamily: "'Baloo 2', cursive", fontSize: "clamp(28px, 4vw, 44px)", color: TEAL_DARK, margin: "0 0 12px" }}>
              ¿Hablamos?
            </h2>
            <p style={{ fontSize: 16, color: "#888", maxWidth: 460, margin: "0 auto" }}>
              Ya seas alumno, profesor o academia, te contamos cómo Solfeante puede encajar en tu día a día musical.
            </p>
          </div>
          <ContactForm />
        </div>
      </section>

      {/* ── FOOTER ── */}
      <footer style={{ background: TEAL_DARK, color: "white", padding: "40px 5vw", textAlign: "center" }}>
        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 10, marginBottom: 16 }}>
          <SolfiMascot size={36} />
          <span style={{ fontFamily: "'Baloo 2', cursive", fontSize: 22, fontWeight: 900 }}>Solfeante</span>
        </div>
        <p style={{ opacity: 0.6, fontSize: 13, margin: "0 0 12px" }}>
          La plataforma de lenguaje musical con IA para conservatorios y academias
        </p>
        <div style={{ display: "flex", gap: 20, justifyContent: "center", flexWrap: "wrap", fontSize: 13, opacity: 0.7 }}>
          <span style={{ cursor: "pointer" }} onClick={() => window.open("https://plataforma.solfeante.com", "_blank")}>Plataforma</span>
          <span>·</span>
          <span style={{ cursor: "pointer" }} onClick={() => scrollTo("contacto")}>Contacto</span>
          <span>·</span>
          <span>hola@solfeante.com</span>
          <span>·</span>
          <span>Política de privacidad</span>
        </div>
        <p style={{ opacity: 0.35, fontSize: 12, marginTop: 20 }}>© {new Date().getFullYear()} Solfeante · Todos los derechos reservados</p>
      </footer>
    </div>
  );
}
